import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {Fragment, useState} from 'react';
import _ from 'lodash';
import moment from 'moment';
import config from '../../../../app/config';
import clsx from 'clsx';
import LinkIcon from '@mui/icons-material/Link';
import {useDispatch} from 'react-redux';
import {setSnackbar} from '../../../../store/reducers/globals';
import {getTableScrollbar} from '../../../../util/scrollbar';
import {AgyleTab, AgyleTabs} from '../../../../components/ui/tabs/agyleTabs';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import InformationBox from '../../../../components/other/informationBox';
import WarningIcon from '@mui/icons-material/Warning';
import FormLabel from '@mui/material/FormLabel';
import {useIceStyles} from './useIceStyles';

const TabPanel = (props) => {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box style={{paddingTop: 10}}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const IceDetails = (props) => {
    const classes = useIceStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Report state
    const [report, setReport] = useState(props.report);
    const [prevReport, setPrevReport] = useState([]);

    // Tabs
    const [value, setValue] = useState('findings');

    // History results state
    const [isHistoryOpen, openHistory, closeHistory] = useOpenState(false, true);
    const [history, setHistory] = useState([]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // Find history value for selected result
    const findHistoryValue = (row) => {
        let historyFind = [];

        function createHistoryElement(iceReport, rep) {
            return {
                ...rep,
                samplesCollectedDateTime: iceReport?.request?.samples[0]?.collectedDateTime,
                sampleCode: iceReport?.request?.samples[0]?.code,
            };
        }

        if (!_.isEmpty(props.allReports)) {
            _.forEach(props.allReports, iceReport => {
                const rep = _.find(iceReport.results, {rubric: row.rubric});
                if (rep && !iceReport?.hidden) {
                    historyFind.push(createHistoryElement(iceReport, rep));
                }
            });
        }

        setHistory(historyFind);
        openHistory();
    };

    const renderRange = (range) => {
        if (range) {
            if (!range?.lowerLimit) {
                return `< ${range?.upperLimit}`;
            }

            if (!range?.upperLimit) {
                return `${range?.lowerLimit} >`;
            }

            return `${range?.lowerLimit} - ${range?.upperLimit}`;
        }
    };

    const renderResultTable = () => {
        const renderComments = (report) => {
            if (report && report.length > 0) {
                let text = [];
                _.forEach(report, (comment, index) => {
                    text.push(<span>{comment.comment}</span>);
                });
                return text;
            }
            return [];
        };

        const result = report ? report.results : null;
        if (result) {
            return (
                <TableContainer className={clsx(getTableScrollbar(), classes.table)}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.header}>
                                <TableCell size="small" className={clsx(classes.headCell, 'left')}/>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Name')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Type')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Value')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Unit')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Range')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'right')}
                                >
                                    {t('Comments')}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {result.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={classes.tableRow}
                                    onClick={() => findHistoryValue(row)}
                                    style={{paddingBottom: 10, paddingTop: 10}}
                                >
                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'left')}
                                    >
                                        {row.abnormal && renderAbnormal()}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.rubric}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.type}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.value}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.uomCode}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {renderRange(row.range)}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'right')}
                                    >
                                        {renderComments(row.comments).map(comment => (
                                                <div key={Math.random()}>
                                                    {comment}
                                                    <br/>
                                                </div>
                                            ),
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
    };

    const renderReportComments = () => {
        if (!_.isEmpty(report?.comments)) {
            let text = '';
            _.forEach(report.comments, comment => {
                text += comment.comment + ' ';
            });
            return text;
        }
    };

    // Load selected linked investigation
    const loadReport = (row) => {
        if (row?.id) {
            const foundReport = _.find(props.allReports, {id: row.id});
            if (foundReport && !foundReport?.hidden) {
                let clone = _.cloneDeep(prevReport);
                clone.push(report);
                setPrevReport(clone);
                setReport({
                    ...foundReport,
                    disciplineExpansion: report?.discipline?.expansion,
                    investigationsDescription: report?.investigations[0]?.description,
                    clinician: report?.request?.clinician?.givenName + ' ' + report?.request?.clinician?.familyName,
                    samplesReceivedDateTime: report?.request?.samples[0]?.receivedDateTime,
                    samplesCollectedDateTime: report?.request?.samples[0]?.collectedDateTime,
                    sampleCode: report?.request?.samples[0]?.code,
                    location: report?.request?.location?.name,
                });
                setValue('findings');
            } else {
                dispatch(setSnackbar({type: 'warning', message: 'Can not find investigation.'}));
            }
        }
    };

    // Load previous investigation
    const loadPrev = () => {
        if (!_.isEmpty(prevReport)) {
            let clone = _.cloneDeep(prevReport);
            setReport(clone.pop());
            setValue('findings');
            setPrevReport(clone);
        }
    };

    // Load External Resources
    const loadExternal = (url) => {
        if (url) {
            props.external(url);
        }
    };

    const sortHistory = (history) => {
        return _.isEmpty(history) ? [] : _.orderBy(history, ['samplesCollectedDateTime'], ['desc']);
    };

    const renderAbnormal = () => {
        return (
            <div className={classes.abnormal}>
                <WarningIcon fontSize="small"/>
            </div>
        );
    };

    const renderLinkedReports = () => {
        if (!_.isEmpty(report?.linkedReports)) {
            return (
                <TableContainer className={clsx(getTableScrollbar(), classes.table)} style={{width: '100%'}}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'left')}
                                >
                                    {t('Discipline')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Report Date')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'right')}
                                >
                                    {t('Reason')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report?.linkedReports.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={classes.tableRow}
                                    onClick={() => loadReport(row)}
                                    style={{paddingBottom: 10, paddingTop: 10}}
                                >
                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'left')}
                                    >
                                        {row?.investigations[0]?.description ? _.capitalize(row.investigations[0].description) : ''}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row?.dateTimeReported ? moment(row.dateTimeReported).format(config.defaultDateTimeFormat) : '-'}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'right')}
                                    >
                                        {row?.hideReason || ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        return (
            <InformationBox type="info">
                {t('No related investigation was found.')}
            </InformationBox>
        );
    };

    const renderExternalTable = () => {
        if (!_.isEmpty(report?.externalResources)) {
            return (
                <TableContainer className={clsx(getTableScrollbar(), classes.table)} style={{width: '100%'}}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'left')}
                                >
                                    {t('Description')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Date Added')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'right')}
                                />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {report?.externalResources.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={classes.tableRow}
                                    style={{paddingBottom: 10, paddingTop: 10}}
                                >
                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'left')}
                                    >
                                        {row?.name ? row.name : ''}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row?.dateAdded ? moment(row.dateAdded).format(config.defaultDateTimeFormat) : '-'}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'right')}
                                    >
                                        <AgyleButton
                                            size="small"
                                            colour="primary"
                                            onClick={() => loadExternal(row?.url)}
                                            startIcon={<LinkIcon/>}
                                            style={{marginLeft: 'auto'}}
                                        >
                                            {t('Link')}
                                        </AgyleButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        return (
            <InformationBox type="info">
                {t('No external resources was found.')}
            </InformationBox>
        );
    };

    const buttons = [];
    if (prevReport?.length > 0) {
        buttons.push(
            <AgyleButton
                variant={'contained'}
                style={{width: 'auto'}}
                onClick={loadPrev}
            >
                {t('Load Previous Investigation')}
            </AgyleButton>,
        );
    }

    return (
        <AgyleDialog
            open={props.open}
            onClose={props.onClose}
            width="lg"
            fullWidth
            title={`${_.capitalize(report?.investigationsDescription)} [${[report?.orderId]}]`}
            buttons={buttons}
            disableSave
            contentStyle={classes.modalBackground}
        >

            <AgyleTabs value={value} onChange={handleChange} variant="scrollable">
                <AgyleTab label={t('Findings')} value={'findings'}/>
                <AgyleTab label={t('Information')} value={'info'}/>
                <AgyleTab label={t('Linked Investigations')} value={'linkedReports'}/>
                <AgyleTab label={t('External Resources')} value={'external'}/>
            </AgyleTabs>

            <div className={classes.groupBar} style={{background: `#${report?.colour}`}}/>

            <TabPanel value={value} index="info" className={classes.root}>
                <>
                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Reported Date')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {moment(report?.dateTimeReported).format(config.defaultDateTimeFormat)}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Ordering Clinician')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {_.capitalize(report?.request?.clinician?.title)} {report?.clinician}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Specialty Code')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {report?.specialtyCode}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Location')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {_.capitalize(report?.request?.location?.name)}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Status')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {report?.status}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Abnormal')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {report?.abnormal ? 'Yes' : 'No'} {report?.abnormal && renderAbnormal()}
                        </div>
                    </div>

                    <div className={classes.invInfo}>
                        <FormLabel style={{marginRight: 10}}>
                            {t('Comments')}
                        </FormLabel>

                        <div className={classes.invInfoText}>
                            {renderReportComments()}
                        </div>
                    </div>
                </>

                <FormLabel>
                    {t('Samples')}
                </FormLabel>

                <TableContainer className={clsx(getTableScrollbar(), classes.table)} style={{maxHeight: 120}}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.header}>
                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'left')}
                                >
                                    {t('Code')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Source')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={classes.headCell}
                                >
                                    {t('Collected Date & Time')}
                                </TableCell>

                                <TableCell
                                    size="small"
                                    className={clsx(classes.headCell, 'right')}
                                >
                                    {t('Received Date & Time')}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {report?.request?.samples.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={classes.tableRow}
                                    style={{paddingBottom: 10, paddingTop: 10}}
                                >
                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'left')}

                                    >
                                        {row.code}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.text}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.rowCell}
                                    >
                                        {row.collectedDateTime ? moment(row.collectedDateTime).format(config.defaultDateTimeFormat) : '-'}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={clsx(classes.rowCell, 'right')}
                                    >
                                        {row.receivedDateTime ? moment(row.receivedDateTime).format(config.defaultDateTimeFormat) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            <TabPanel value={value} index={'findings'} className={classes.root}>
                {report && (
                    <>
                        {renderResultTable()}
                    </>
                )}
            </TabPanel>

            <TabPanel value={value} index={'linkedReports'}>
                <FormLabel>
                    {t('Linked Investigations')}
                </FormLabel>

                {renderLinkedReports()}
            </TabPanel>

            {/* External Resources */}
            <TabPanel value={value} index={'external'}>
                <FormLabel>
                    {t('External Resources')}
                </FormLabel>
                {renderExternalTable()}
            </TabPanel>

            {/* History Panel */}
            <AgyleDialog
                open={isHistoryOpen}
                width="sm"
                fullWidth
                onClose={closeHistory}
                title={history[0]?.rubric}
                disableSave
            >
                <>
                    <InformationBox type="info">
                        {t('Value Range: ')} {renderRange(history[0]?.range)}
                    </InformationBox>

                    <TableContainer
                        className={clsx(getTableScrollbar(), classes.table)}
                        style={{maxHeight: 380}}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow className={classes.header}>
                                    <TableCell
                                        size="small"
                                        className={clsx(classes.headCell, 'left')}
                                    />

                                    <TableCell
                                        size="small"
                                        className={classes.headCell}
                                    >
                                        {t('Samples Collected Date')}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.headCell}
                                    >
                                        {t('Value')}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={classes.headCell}
                                    >
                                        {t('Unit')}
                                    </TableCell>

                                    <TableCell
                                        size="small"
                                        className={clsx(classes.headCell, 'right')}
                                    >
                                        {t('Sample Code')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {sortHistory(history).map((row, index) => (
                                    <TableRow
                                        key={row.id}
                                        className={classes.tableRow}
                                        style={{paddingBottom: 10, paddingTop: 10}}
                                    >
                                        <TableCell
                                            size="small"
                                            className={clsx(classes.rowCell, 'left')}
                                        >
                                            {row?.abnormal && renderAbnormal()}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.samplesCollectedDateTime ? moment(row.samplesCollectedDateTime).format(config.defaultDateTimeFormat) : '-'}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.value}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={classes.rowCell}
                                        >
                                            {row.uomCode}
                                        </TableCell>

                                        <TableCell
                                            size="small"
                                            className={clsx(classes.rowCell, 'right')}
                                        >
                                            {row.sampleCode}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            </AgyleDialog>
        </AgyleDialog>
    );
};

export default IceDetails;